package com.siriusxm.pia.rest.unifiedaggregator

import com.siriusxm.pia.ApplicationContext
import unifiedcontent.aggregator.*
import io.ktor.client.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlin.time.Instant

@Deprecated("Use the Aggregator SDK class directly (e.g. via AggregatorService.clientApi)")
interface UnifiedAggregatorClient {

    suspend fun fetchEntity(
        id: String,
        type: String,
        version: String
    ): JsonObject

    suspend fun fetchEntityById(
        id: String,
        includeRemoved: String? = null
    ): List<JsonObject>

    suspend fun incomingEntity(
        id: String,
        type: String,
        version: String,
        fields: String? = null
    ): IncomingEntityResult?

    /**
     * Get the outgoing entity by id, type and version
     */
    suspend fun outgoing(
        id: String,
        type: String,
        version: String
    ): JsonObject?

    /**
     * Get the outgoing entity
     */
    suspend fun getOutgoingEntity(
        id: String
    ): JsonArray

    /**
     * Republish an outgoing entity.
     */
    suspend fun republish(
        id: String,
        type: String,
        version: String,
        updateTimestamp: Boolean = false
    )

    suspend fun searchEntities(
        queryTerm: String? = null,
        types: String? = null,
        from: Int? = null,
        size: Int? = null,
        visible: Boolean? = null
    ): EntityListResponse

    suspend fun lookupSourceId(id: String): MappingResponse

    /**
     * Get the list of the user's service roles for producers and consumers
     */
    suspend fun serviceRoles(): ServiceUserMembershipList

    /**
     * Get the user's service memberships and aggregated permissions
     */
    suspend fun userPermissions(): UserPermissions

    /**
     * Create a new producer
     */
    suspend fun newProducer(producerDetails: ProducerDetails)

    /**
     * Get users for a producer
     */
    suspend fun internalProducerUsers(producerId: String): ServiceUsersList

    /**
     * Create a new consumer
     */
    suspend fun newConsumer(consumerDetails: ConsumerDetails)

//    /**
//     * Get a list of backfills
//     */
//    suspend fun backfills(
//        since: String? = null,
//        until: String? = null
//    ): List<BackfillStatus>
//
//    /**
//     * Get all active backfills
//     */
//    suspend fun activeBackfills(): List<BackfillStatus>
//
//    /**
//     * Get status for a backfill by id
//     */
//    suspend fun backfill(id: String): BackfillStatus
//
//    /**
//     * Start a backfill
//     */
//    suspend fun startBackfill(request: BackfillRequest): BackfillStatus
//
//    /**
//     * Cancel a backfill
//     */
//    suspend fun cancelBackfill(id: String)

    suspend fun relationshipSchemas(): List<RelationshipSchema>


    suspend fun relationshipSubjects(
        relationshipSchemaId: String,
        id: String,
        descending: Boolean? = null,
        limit: Int? = null,
        paginationToken: String? = null,
    ): EntityListResponse

    suspend fun relationshipObjects(
        relationshipSchemaId: String,
        id: String,
        descending: Boolean? = null,
        limit: Int? = null,
        paginationToken: String? = null,
    ): EntityListResponse

    /**
     * Gets events for an entity type.
     */
    suspend fun entityTypeEvents(
        type: String,
        queryId: String? = null,
        startTime: Instant? = null,
        endTime: Instant? = null,
        option: EventQueryOption? = null,
        limit: Int? = null
    ): EventSearchStatus

    suspend fun submitRelationship(
        relationshipSchemaId: String,
        update: IncomingRelationshipUpdate
    )

//    suspend fun testBackfill(backfillTestRequest: BackfillTestRequest): BackfillTestResponse

    suspend fun deleteScheduledPartial(
        entityId: String,
        entityType: String,
        entityVersion: String,
        partialId: String,
        timestamp: String
    )

    suspend fun deleteScheduledUpdate(
        entityId: String,
        entityType: String,
        entityVersion: String,
        timestamp: String
    )

    /**
     * Get a consumer. Unlike the consumer list call, this version includes the targets.
     */
    suspend fun internalConsumer(consumerId: String): ConsumerDetails

    /**
     * Get a consumer User. Unlike the consumer list call, this version includes the targets.
     */
    suspend fun internalConsumerUsers(consumerId: String): ServiceUsersList

    /**
     * Add a new target to the consumer. Use addTarget which handles encoding the consumer id.
     */
    suspend fun internalAddTarget(
        consumerId: String,
        request: AddConsumerTarget
    ): ConsumerDetails

}

/**
 * Get a consumer. Unlike the consumer list call, this version includes the targets.
 */
suspend fun UnifiedAggregatorClient.getConsumer(consumerId: String): ConsumerDetails {
    return this.internalConsumer(consumerId)
}

/**
 * Get a consumer. Unlike the consumer list call, this version includes the targets.
 */
suspend fun UnifiedAggregatorClient.getProducerUsers(producerId: String): ServiceUsersList {
    return this.internalProducerUsers(producerId)
}

/**
 * Add a new target to the consumer
 */
suspend fun UnifiedAggregatorClient.addTarget(consumerId: String, request: AddConsumerTarget): ConsumerDetails {
    return this.internalAddTarget(consumerId, request)
}

/**
 * Get the default client for the unified content aggregator, using tokens provided by the context.
 */
fun unifiedAggregatorClient(context: ApplicationContext): AggregatorSDKClient {
    val httpClient = HttpClient {
        install(ContentNegotiation) {
            json(Json {
                isLenient = true
                ignoreUnknownKeys = true
            })
        }
        context.installAuth(this)
    }

    val aggregator = Aggregator(context.configuration.unifiedAggregatorApiUrl, httpClient)
    return AggregatorSDKClient(sdk = aggregator)
}
